/**
 * 移动端工具函数
 */

/**
 * 判断是否在微信环境（个人微信，排除企业微信）
 */
export const isWechat = (): boolean => {
  return /micromessenger/i.test(navigator.userAgent) && !/wxwork/i.test(navigator.userAgent)
}

/**
 * 判断是否在企微环境
 */
export const isWorkWechat = (): boolean => {
  return /wxwork/i.test(navigator.userAgent)
}

/**
 * 判断是否在微信或企业微信环境
 * 微信 JS-SDK 在这两种环境下都可用
 */
export const isWechatFamily = (): boolean => {
  return /micromessenger/i.test(navigator.userAgent)
}

/**
 * 判断是否在 iOS 环境
 */
export const isIOS = (): boolean => {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

/**
 * 判断是否在 Android 环境
 */
export const isAndroid = (): boolean => {
  return /android/i.test(navigator.userAgent)
}

/**
 * 判断是否在移动端环境
 */
export const isMobile = (): boolean => {
  return /mobile|android|iphone|ipad|ipod/i.test(navigator.userAgent)
}

/** 网络连接类型 */
interface NetworkConnection {
  effectiveType?: string
}

/**
 * 获取网络状态
 */
export const getNetworkType = (): string => {
  const connection =
    (navigator as { connection?: NetworkConnection }).connection ||
    (navigator as { mozConnection?: NetworkConnection }).mozConnection ||
    (navigator as { webkitConnection?: NetworkConnection }).webkitConnection
  return connection?.effectiveType || 'unknown'
}

/**
 * 判断网络是否在线
 */
export const isOnline = (): boolean => {
  return navigator.onLine
}

/**
 * 监听网络状态变化
 */
export const onNetworkChange = (callback: (online: boolean) => void): (() => void) => {
  const handleOnline = () => callback(true)
  const handleOffline = () => callback(false)

  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  return () => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
  }
}

/**
 * 获取设备像素比
 */
export const getDevicePixelRatio = (): number => {
  return window.devicePixelRatio || 1
}

/**
 * 获取视口宽度
 */
export const getViewportWidth = (): number => {
  return window.innerWidth
}

/**
 * 获取视口高度
 */
export const getViewportHeight = (): number => {
  return window.innerHeight
}

/**
 * 获取安全区域
 */
export const getSafeAreaInsets = (): { top: number; bottom: number; left: number; right: number } => {
  const style = getComputedStyle(document.documentElement)
  return {
    top: parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0'),
    bottom: parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0'),
    left: parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0'),
    right: parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0'),
  }
}

/**
 * 保存滚动位置到 sessionStorage
 */
export const saveScrollPosition = (key: string): void => {
  const scrollY = window.scrollY || document.documentElement.scrollTop
  sessionStorage.setItem(`scroll_${key}`, scrollY.toString())
}

/**
 * 从 sessionStorage 恢复滚动位置
 */
export const restoreScrollPosition = (key: string): void => {
  const scrollY = sessionStorage.getItem(`scroll_${key}`)
  if (scrollY) {
    setTimeout(() => {
      window.scrollTo(0, parseInt(scrollY))
    }, 0)
  }
}

/**
 * 清除滚动位置
 */
export const clearScrollPosition = (key: string): void => {
  sessionStorage.removeItem(`scroll_${key}`)
}

/**
 * 防止页面回弹（iOS）
 */
export const preventBounce = (enable: boolean): void => {
  if (!isIOS()) return

  const style = document.body.style
  if (enable) {
    style.overflow = 'hidden'
    style.position = 'fixed'
    style.width = '100%'
  } else {
    style.overflow = ''
    style.position = ''
    style.width = ''
  }
}

/**
 * 复制到剪贴板
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    } else {
      // 兼容旧版浏览器
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      return success
    }
  } catch {
    return false
  }
}

/**
 * 唤起拨号
 */
export const makeCall = (phoneNumber: string): void => {
  window.location.href = `tel:${phoneNumber}`
}

/**
 * 判断是否支持触摸事件
 */
export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

/**
 * 获取点击事件的坐标（兼容触摸和鼠标）
 */
export const getEventPosition = (e: React.TouchEvent | React.MouseEvent): { x: number; y: number } => {
  if ('touches' in e) {
    return {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    }
  }
  return {
    x: e.clientX,
    y: e.clientY,
  }
}
